import type { TutorialStep } from "./tutorialScript";

/**
 * Second parcours (voir aussi `TUTORIAL_STEPS`), meme format et meme mecanique :
 * chaque etape est une position FEN autonome, la surbrillance est calculee par le
 * vrai moteur de regles (`legalMovesFrom`), jamais ecrite a la main. Contrairement
 * au tutoriel de regles, celui-ci enseigne des principes strategiques, pas les
 * deplacements de base.
 *
 * Deux contraintes a respecter pour toute nouvelle etape :
 * - les deux rois doivent toujours etre presents sur l'echiquier (`findKing` leve
 *   sinon une exception dans `legalMovesFrom`) ;
 * - `selectSquare` ne renvoie des cases que si son camp correspond au trait FEN
 *   (`w`/`b`) — pour une piece dont on veut juste montrer la portee sans dependre
 *   du trait, preferer `targetSquares` (surbrillance statique, sans passer par le
 *   moteur).
 *
 * `difficulty` filtre les etapes affichees selon le niveau choisi dans
 * `showDifficultyModal` (cf. `main.ts`) : les etapes sans `difficulty` (intro,
 * transition) restent visibles quel que soit le niveau.
 */
export const STRATEGY_TUTORIAL_STEPS: readonly TutorialStep[] = [
  {
    fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    narration:
      "Les regles, tu les as en main, l'ami — mais gagner, c'est autre chose ! Voici quelques principes que les meilleurs joueurs suivent depuis des siecles. Suis le guide, ca vaut tout l'or de la taverne.",
  },
  {
    fen: "rnbqkbnr/pppp1ppp/8/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 2 2",
    narration:
      "Regle numero un : bats-toi pour le centre ! Les cases d4, e4, d5 et e5 valent de l'or — une piece qui les controle voit tout l'echiquier et gagne en mobilite. Avance tes pions centraux des le debut.",
    difficulty: "debutant",
    targetSquares: ["d4", "e4", "d5", "e5"],
  },
  {
    fen: "r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 4 3",
    narration:
      "Sors tes cavaliers et tes fous avant de bouger la dame ou de lancer une attaque prematuree. Chaque piece developpee, c'est une arme de plus prete au combat — regarde ce fou, deja actif sur sa diagonale.",
    difficulty: "debutant",
    selectSquare: "c4",
  },
  {
    fen: "r1bqk1nr/pppp1ppp/2n5/2b1p3/2B1P3/5N2/PPPP1PPP/RNBQ1RK1 b kq - 6 4",
    narration:
      "Mets ton roi a l'abri, et vite ! En general via le roque, comme les Blancs viennent de le faire ici. Un roi oublie au centre est une cible revee pour l'adversaire — ne tarde pas trop avant de roquer.",
    difficulty: "debutant",
  },
  {
    fen: "2r1k1r1/4N3/8/8/8/8/8/4K3 w - - 0 1",
    narration:
      "Voici une fourchette ! Ce cavalier attaque en meme temps les deux tours noires : quoi qu'il arrive, l'une d'elles tombera. Le cavalier, avec ses sauts en L, est un maitre pour ce genre de coup a deux menaces.",
    difficulty: "debutant",
    selectSquare: "e7",
  },
  {
    fen: "4k3/8/2n5/1B6/8/8/8/4K3 b - - 0 1",
    narration:
      "Un clouage : le fou blanc cloue le cavalier noir contre son roi. Regarde bien — en selectionnant le cavalier, aucune case ne s'illumine ! Il ne peut legalement bouger nulle part : s'il bougeait, son roi se retrouverait en echec.",
    difficulty: "debutant",
    selectSquare: "c6",
  },
  {
    fen: "4q3/8/8/8/4k3/8/8/4R3 b - - 0 1",
    narration:
      "Une enfilade, maintenant. La tour blanche attaque le roi noir sur la colonne e — il doit bouger. Mais juste derriere lui, sans defense, se trouve la dame noire : une fois le roi ecarte, la tour la capturera au coup suivant.",
    difficulty: "debutant",
    targetSquares: ["e1", "e4", "e8"],
  },
  {
    fen: "3k4/2r5/8/3N4/8/8/8/3RK3 w - - 0 1",
    narration:
      "Une attaque a la decouverte, l'ami ! Ce cavalier blanc bloque sa propre tour. S'il bouge — en capturant la tour noire en c7, par exemple — il degage la colonne d, et sa tour met aussitot le roi noir en echec. Deux menaces d'un seul coup, ca ne se refuse pas !",
    difficulty: "debutant",
    selectSquare: "d5",
  },
  {
    fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    narration:
      "Voila les fondamentaux, l'ami : centre, developpement, roi a l'abri, et l'oeil ouvert pour les fourchettes, clouages, enfilades et attaques a la decouverte. Pret pour la suite ? Passons a des principes plus avances, ceux qui separent les joueurs confirmes du reste.",
    difficulty: "debutant",
  },
  {
    fen: "4k3/1p3p2/8/3N4/8/8/8/4K3 w - - 0 1",
    narration:
      "Un avant-poste, maintenant : ce cavalier blanc trone en d5, au coeur du jeu adverse. Regarde ses cases — et remarque qu'aucun pion noir, sur les colonnes c ou e, ne pourra jamais venir le chasser. Une piece qu'on ne peut plus deloger vaut son pesant d'or, surtout plantee au centre.",
    difficulty: "avance",
    selectSquare: "d5",
  },
  {
    fen: "4k3/8/8/4P3/8/8/8/4K3 w - - 0 1",
    narration:
      "Un pion passe, ensuite : plus aucun pion noir, ni sur sa colonne ni sur les colonnes voisines, ne peut l'arreter en le capturant. Rien ne se dresse plus entre lui et la promotion — pousse-le, surtout escorte par ton roi, et il finira dame tot ou tard.",
    difficulty: "avance",
    targetSquares: ["e6", "e7", "e8"],
  },
  {
    fen: "6k1/R4ppp/8/8/8/8/8/6K1 w - - 0 1",
    narration:
      "Une tour sur la 7e rangee, c'est redoutable : regarde, elle attaque trois pions d'un coup et coince le roi noir sur sa derniere rangee, incapable d'avancer. On dit qu'elle est 'sur la 7e' — installe toujours tes tours la des que la colonne s'ouvre, l'occasion se presente rarement deux fois.",
    difficulty: "avance",
    selectSquare: "a7",
  },
  {
    fen: "8/8/4k3/4P3/4K3/8/8/8 w - - 0 1",
    narration:
      "Dans les finales de rois et pions, retiens le mot 'opposition' : quand les deux rois se font face avec un nombre impair de cases entre eux, celui qui n'a PAS a jouer tient l'avantage — l'autre doit reculer ou s'ecarter, et cede du terrain malgre lui. Un principe discret, mais qui decide d'innombrables finales.",
    difficulty: "avance",
  },
  {
    fen: "6k1/8/8/8/8/2n5/1P6/3R2K1 w - - 0 1",
    narration:
      "Un principe plus subtil pour finir : le sacrifice de la qualite. Donner une tour (5 points) contre un cavalier (3 points) semble une perte — mais si ce cavalier tient une case cle ou etouffe toute ton attaque, l'echanger peut valoir bien plus que sa valeur en points. Ne compte pas que le materiel : compte aussi l'activite des pieces qui restent sur l'echiquier.",
    difficulty: "avance",
    targetSquares: ["c3"],
  },
  {
    fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    narration:
      "Voila pour les principes avances, l'ami : avant-postes, pions passes, tours sur la 7e rangee, opposition dans les finales, et parfois meme sacrifier la qualite pour l'activite. Ajoute ca a tes fondamentaux, entraine-toi sur les exercices, et observe chaque partie que tu joues — c'est ainsi qu'on progresse, coup apres coup. A l'echiquier, maintenant !",
    difficulty: "avance",
  },
];
